import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {api} from "../../../api/api";
import {EvChargingQuarterSummary} from "../../../api/EvChargingApi";
import DataGrid from "../../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import IconLarge from "../../../hiberUiKit/src/atoms/Icon/IconLarge";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import "../../v5/table/workspace/workspaceContainer.scss";
import Loading from "../../ui/loading/Loading";
import Routes from "../../../navigation/route/Route";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {formatCredits, formatKwh, formatDate, formatPeriod, lateBillsLabel, quarterLabel} from "../evChargingBill";
import {evColors, evMessageStyles} from "../evChargingStyles";
import QuarterState from "./QuarterState";

interface State {
	quarters?: EvChargingQuarterSummary[];
	failed: boolean;
	/**
	 * Quarters whose file is on its way, so a second click does not start another download.
	 */
	downloading: string[];
}

type Props = RouteComponentProps<{}>;

/**
 * RTFO credit submissions: a quarter per row with what its NORA claim adds up to now, and its files. Laid out like the
 * workspace lists (Transactions, Jobs): their title bar, full width, the whole row opens the quarter.
 */
class EvChargingQuartersPage extends React.Component<Props, State> {
	state: State = {failed: false, downloading: []};

	private unmounted = false;

	componentDidMount() {
		api.instance.evChargingApi.getQuarters()
			.then(quarters => !this.unmounted && this.setState({quarters}))
			.catch(error => {
				if (!this.unmounted) this.setState({failed: true});
				notifyBugsnagAndShowToast(error, "Fetch quarters");
			});
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	render() {
		return (
			<div className={"c-workspace"}>
				<div className={"c-workspace-title"}>
					<h1>
						<IconLarge icon={Icon.Quote}/>
						<span>RTFO credit submissions</span>
					</h1>
				</div>
				<Loading isLoading={!this.state.quarters && !this.state.failed}>
					{this.renderQuarters()}
				</Loading>
			</div>
		);
	}

	private renderQuarters() {
		const {quarters, failed} = this.state;
		return (
			<DataGrid hasHover={true} hasPointer={true}>
				<thead>
				<tr>
					<th>QUARTER</th>
					<th>STATUS</th>
					<th>NORA DEADLINE</th>
					<th className={"t-right"}>METERS</th>
					<th className={"t-right"}>JOBS</th>
					<th className={"t-right"}>ENERGY (KWH)</th>
					<th className={"t-right"}>CREDITS</th>
					<th/>
				</tr>
				</thead>
				<tbody>
				{failed && this.renderMessage(
					<span style={{color: evColors.error}}>The quarters could not be loaded. Refresh the page to try again.</span>)}
				{quarters && quarters.length === 0 && this.renderMessage("No EV charging jobs yet.")}
				{quarters && quarters.map(quarter => (
					<tr key={quarter.id} onClick={() => this.props.history.push(Routes.EvChargingQuarter(quarter.id))}>
						<td>
							<div style={{fontWeight: 600}}>{quarterLabel(quarter.year, quarter.quarter)}</div>
							<div style={{fontSize: 12, color: evColors.muted}}>
								{formatPeriod(quarter.firstDay, quarter.lastDay)}
							</div>
						</td>
						<td><QuarterState state={quarter.state}/></td>
						<td>{formatDate(quarter.submissionDeadline)}</td>
						<td className={"t-right"}>{quarter.meterPointCount}</td>
						<td className={"t-right"} style={{whiteSpace: "nowrap"}}>
							{quarter.lateBillCount > 0 &&
							<span style={{...evMessageStyles.chip, marginRight: 8}}>{lateBillsLabel(quarter.lateBillCount)}</span>}
							{quarter.billCount}
						</td>
						<td className={"t-right"}>{formatKwh(quarter.totalKwh)}</td>
						<td className={"t-right"}>{formatCredits(quarter.totalCredits)}</td>
						{/* clicks stay in this cell, so downloading does not open the quarter as well */}
						<td className={"t-right"} onClick={e => e.stopPropagation()}>
							<ButtonSecondary label={"Download ZIP"} onClick={() => this.download(quarter)}
											 disabled={this.state.downloading.indexOf(quarter.id) >= 0}/>
						</td>
					</tr>
				))}
				</tbody>
			</DataGrid>
		);
	}

	/**
	 * In the table, as the portal's lists show theirs, so the header stays where it is.
	 */
	private renderMessage(message: React.ReactNode) {
		return (
			<tr>
				<td colSpan={COLUMNS} style={styles.message}>{message}</td>
			</tr>
		);
	}

	private download(quarter: EvChargingQuarterSummary) {
		this.setState(state => ({downloading: [...state.downloading, quarter.id]}));
		const done = () => !this.unmounted &&
			this.setState(state => ({downloading: state.downloading.filter(id => id !== quarter.id)}));
		api.instance.evChargingApi.downloadStatementOfSupply(quarter)
			.then(done)
			.catch(error => {
				done();
				notifyBugsnagAndShowToast(error, "Download Statement of Supply");
			});
	}
}

// RestrictedRoute renders the page without the router's props
export default withRouter(EvChargingQuartersPage);

const COLUMNS = 8;

const styles: { [key: string]: React.CSSProperties } = {
	message: {textAlign: "center", padding: "48px 0"}
};
